import React from 'react';
import { useImageUrl } from '../../hooks/useImageUrl';

interface PhotoImageProps {
  photoId?: string | number | null;
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  onError?: () => void;
  onLoad?: () => void;
}

export const PhotoImage: React.FC<PhotoImageProps> = ({
  photoId,
  alt = '',
  className = '',
  fallback,
  loadingComponent,
  onError,
  onLoad,
}) => {
  const { imageUrl, loading, error, refetch } = useImageUrl(photoId);

  React.useEffect(() => {
    if (error && onError) {
      onError();
    }
  }, [error, onError]);

  React.useEffect(() => {
    if (imageUrl && !loading && !error && onLoad) {
      onLoad();
    }
  }, [imageUrl, loading, error, onLoad]);

  if (loading) {
    return (
      <div className={className}>
        {loadingComponent || (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 animate-pulse">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={className}>
        {fallback || (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <svg
              className="w-1/2 h-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
        )}
      </div>
    );
  }

  const handleImageError = () => {
    console.warn(`Image failed to load for photoId: ${photoId}, attempting retry...`);
    refetch();
    onError?.();
  };

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onError={handleImageError}
      onLoad={() => onLoad?.()}
    />
  );
};

export default PhotoImage;
